import { Check, ChevronDown, ChevronUp, Pencil, Plus, X } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { anadirALista, enLista } from "./listas";

/**
 * Lista editable de Ajustes (Robert, 02/11/2026: «no un hueco en blanco donde escribir lo que
 * sea»): cada elemento en su fila, con subir, bajar, renombrar y quitar, y un campo «Añadir»
 * que no deja repetir. Lo nuevo entra antes de «Otro», que se queda el último. Se guarda con
 * «Guardar ajustes».
 */
export const EditorLista = ({
  id,
  titulo,
  ayuda,
  valores,
  cambiar,
  textoAnadir,
  placeholder,
}: {
  id: string;
  titulo: string;
  ayuda?: string;
  valores: string[];
  cambiar: (v: string[]) => void;
  textoAnadir: string;
  placeholder?: string;
}) => {
  const translate = useTranslate();
  const t = (k: string, o?: object) =>
    translate(`nevora.ajustes.lista.${k}`, o);
  const [nuevo, setNuevo] = useState("");
  const [editando, setEditando] = useState<number | null>(null);
  const [texto, setTexto] = useState("");
  const [error, setError] = useState<string | null>(null);

  const anadir = () => {
    const v = nuevo.trim();
    if (!v) return;
    if (enLista(valores, v)) {
      setError(t("repetido", { valor: v }));
      return;
    }
    cambiar(anadirALista(valores, v));
    setNuevo("");
    setError(null);
  };
  const mover = (i: number, paso: -1 | 1) => {
    const j = i + paso;
    if (j < 0 || j >= valores.length) return;
    const copia = [...valores];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    cambiar(copia);
  };
  const quitar = (i: number) => {
    cambiar(valores.filter((_, k) => k !== i));
    setEditando(null);
  };
  const guardarEdicion = (i: number) => {
    const v = texto.trim();
    if (!v) return;
    if (enLista(valores, v, i)) {
      setError(t("repetido", { valor: v }));
      return;
    }
    cambiar(valores.map((x, k) => (k === i ? v : x)));
    setEditando(null);
    setError(null);
  };

  return (
    <section className="superficie space-y-4 p-5" data-testid={`lista-${id}`}>
      <div className="space-y-0.5">
        <h2 className="titulo-seccion">{titulo}</h2>
        {ayuda ? (
          <p className="text-xs text-muted-foreground">{ayuda}</p>
        ) : null}
      </div>
      <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/70">
        {valores.length ? (
          valores.map((v, i) => (
            <li
              key={`${v}-${i}`}
              className="group flex items-center gap-2 bg-card px-3 py-1.5"
            >
              <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {i + 1}
              </span>
              {editando === i ? (
                <form
                  className="flex flex-1 items-center gap-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    guardarEdicion(i);
                  }}
                >
                  <Input
                    autoFocus
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && setEditando(null)}
                    aria-label={t("renombrar", { valor: v })}
                    className="h-8"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    aria-label={t("guardar_cambio")}
                  >
                    <Check className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    aria-label={t("cancelar")}
                    onClick={() => setEditando(null)}
                  >
                    <X className="size-4" />
                  </Button>
                </form>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate py-1 text-sm">
                    {v}
                  </span>
                  <span className="flex shrink-0 items-center opacity-70 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      disabled={i === 0}
                      aria-label={t("subir", { valor: v })}
                      onClick={() => mover(i, -1)}
                    >
                      <ChevronUp className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      disabled={i === valores.length - 1}
                      aria-label={t("bajar", { valor: v })}
                      onClick={() => mover(i, 1)}
                    >
                      <ChevronDown className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      aria-label={t("editar", { valor: v })}
                      onClick={() => {
                        setEditando(i);
                        setTexto(v);
                        setError(null);
                      }}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8 text-muted-foreground hover:text-destructive"
                      aria-label={t("quitar", { valor: v })}
                      onClick={() => quitar(i)}
                    >
                      <X className="size-4" />
                    </Button>
                  </span>
                </>
              )}
            </li>
          ))
        ) : (
          <li className="px-3 py-3 text-sm text-muted-foreground">
            {t("vacia")}
          </li>
        )}
      </ul>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          anadir();
        }}
      >
        <Input
          value={nuevo}
          onChange={(e) => {
            setNuevo(e.target.value);
            setError(null);
          }}
          placeholder={placeholder}
          aria-label={textoAnadir}
          className="h-9"
        />
        <Button
          type="submit"
          variant="outline"
          className="h-9 shrink-0 gap-1.5"
          disabled={!nuevo.trim()}
        >
          <Plus className="size-4" aria-hidden />
          {textoAnadir}
        </Button>
      </form>
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
};
